/**
 * Order Types
 */

export type OrderStatus = 
  | 'PENDING' 
  | 'CONFIRMED' 
  | 'ASSIGNED' 
  | 'IN_PROGRESS' 
  | 'COMPLETED' 
  | 'CANCELLED' 
  | 'REJECTED' 
  | 'REFUNDED';

export type OrderType = 'SERVICE' | 'EQUIPMENT';

export type PurchaseType = 'BUY' | 'RENT';

export type BeneficiaryType = 'SELF' | 'OTHER' | 'SAVED';

export interface Order {
  id: number;
  order_number: string;
  user: {
    id: number;
    full_name: string;
    email: string;
    phone_number: string;
  };
  service?: {
    id: number;
    name: string;
    name_ar: string;
  } | null;
  subservice?: {
    id: number;
    name: string;
    name_ar: string;
    description: string;
  } | null;
  internal_subservice?: {
    id: number;
    name: string;
    name_ar: string;
  } | null;
  order_type?: OrderType;
  purchase_type?: PurchaseType | null;
  rental_days?: number | null;
  quantity?: number;
  assigned_provider: {
    id: number;
    type: 'SPECIALIST' | 'CONSULTANT';
    name: string;
    specialization?: string;
    phone?: string;
    email?: string;
    rating?: string;
  } | null;
  beneficiary_type: BeneficiaryType;
  patient_name: string;
  patient_age: number;
  patient_gender: string;
  patient_phone: string;
  health_conditions: string;
  allergies: string;
  current_medications: string;
  special_notes: string;
  location: {
    full_address: string;
    city: string;
    area: string;
    latitude: string | null;
    longitude: string | null;
  };
  scheduled_date: string;
  scheduled_time_from: string;
  scheduled_time_to: string;
  estimated_duration: number | null;
  service_price: string;
  platform_fee: string;
  total_amount: string;
  /** True for Home Doctor Visit orders whose price is still 0 — admin must set the price when confirming. */
  requires_price_input?: boolean;
  daily_rental_price?: string | null;
  rental_total?: string | null;
  status: OrderStatus;
  payment: {
    id: number;
    payment_method: string;
    amount: string;
    status: string;
    paid_at: string | null;
  } | null;
  is_special_request?: boolean;
  preferred_gender?: string | null;
  preferred_age_range?: string | null;
  preferred_language?: string | null;
  result?: import('./orderResult').OrderResult | null;
  patient_file?: number | null;
  review?: import('./review').OrderReview | null;
  /** Completed and not rated yet */
  can_rate?: boolean;
  created_at: string;
  updated_at: string;
}

export interface OrderListItem {
  id: number;
  order_number: string;
  user_name: string;
  user_email: string;
  service_name: string;
  subservice_name: string;
  patient_name: string;
  patient_phone: string;
  requested_provider: {
    id: number;
    type: string;
    name: string;
  } | null;
  assigned_provider: {
    id: number;
    type: string;
    name: string;
  } | null;
  scheduled_date: string;
  scheduled_time_from: string;
  scheduled_time_to: string;
  status: OrderStatus;
  total_amount: string;
  payment_status: string | null;
  is_special_request: boolean;
  patient_file?: number | null;
  service_rating?: number | null;
  created_at: string;
}

export interface OrdersResponse {
  success: boolean;
  count: number;
  next: string | null;
  previous: string | null;
  total_pages: number;
  current_page: number;
  results: OrderListItem[];
}

export interface OrderDetailResponse {
  success: boolean;
  message: string;
  data: Order;
}

// Order Statistics Types
export interface OrderStatisticsOverview {
  total_orders: number;
  orders_today: number;
  orders_this_week: number;
  orders_this_month: number;
  completion_rate: number;
  cancellation_rate: number;
}

export interface OrderStatisticsByStatus {
  PENDING: number;
  CONFIRMED: number;
  IN_PROGRESS: number;
  COMPLETED: number;
  CANCELLED: number;
}

export interface OrderStatisticsByProvider {
  with_specialist: number;
  with_consultant: number;
  unassigned: number;
}

export interface OrderStatisticsRevenue {
  total_revenue: number;
  average_order_value: number;
  completed_revenue: number;
  pending_revenue: number;
}

export interface OrderStatisticsPaymentMethods {
  CASH: number;
  FAWRY: number;
  CREDIT_CARD: number;
}

export interface TopService {
  id: number;
  name: string;
  order_count: number;
  total_revenue: number;
}

export interface TopSubservice {
  id: number;
  name: string;
  order_count: number;
  total_revenue: number;
}

export interface TopSpecialist {
  id: number;
  name: string;
  order_count: number;
  completed_orders: number;
}

export interface TopConsultant {
  id: number;
  name: string;
  order_count: number;
  completed_orders: number;
}

// RecentOrder is now exported from dashboard.ts to avoid duplicate export
// This interface is kept for backward compatibility but should use the one from dashboard
export interface OrderRecentOrder {
  id: number;
  order_number: string;
  user_name: string;
  service_name: string;
  subservice_name: string;
  patient_name: string;
  status: OrderStatus;
  total_amount: string;
  created_at: string;
}

export interface OrderStatistics {
  overview: OrderStatisticsOverview;
  by_status: OrderStatisticsByStatus;
  by_provider: OrderStatisticsByProvider;
  revenue: OrderStatisticsRevenue;
  payment_methods: OrderStatisticsPaymentMethods;
  top_services: TopService[];
  top_subservices: TopSubservice[];
  top_specialists: TopSpecialist[];
  top_consultants: TopConsultant[];
  recent_orders: OrderRecentOrder[];
}

export interface OrderStatisticsResponse {
  success: boolean;
  message: string;
  data: OrderStatistics;
}

export const ORDER_STATUS_CONFIG: Record<OrderStatus, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  PENDING: { label: 'Pending', color: 'warning' },
  CONFIRMED: { label: 'Confirmed', color: 'info' },
  ASSIGNED: { label: 'Assigned', color: 'primary' },
  IN_PROGRESS: { label: 'In Progress', color: 'secondary' },
  COMPLETED: { label: 'Completed', color: 'success' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
  REJECTED: { label: 'Rejected', color: 'error' },
  REFUNDED: { label: 'Refunded', color: 'default' },
};
